import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import clinicService from '../../../../../services/clinic.service';
import webInquiryService from '../../../../../services/webinquiry.service';
import Loading from '../../../../common/Loading';

const InfoRow = ({ label, children }) => (
    <div className="notifications-info-row">
        <span className="notifications-info-label">{label}</span>
        <div className="notifications-info-value">{children}</div>
    </div>
);

const SectionTitle = ({ children, first }) => (
    <div className={`clinic-services-section-title${first ? ' clinic-services-section-title--first' : ''}`}>
        {children}
    </div>
);

const StatusBadge = ({ enabled, labelOn = 'Enabled', labelOff = 'Disabled' }) => (
    <span
        className={`clinic-services-status-badge ${
            enabled ? 'clinic-services-status-badge--enabled' : 'clinic-services-status-badge--disabled'
        }`}
    >
        <i className={`bi ${enabled ? 'bi-check-circle' : 'bi-x-circle'}`}></i>
        {enabled ? labelOn : labelOff}
    </span>
);

function WebInquiryTab() {
    const { clinicId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [featureEnabled, setFeatureEnabled] = useState(false);
    const [settings, setSettings] = useState(null);
    const [domains, setDomains] = useState([]);
    const [recipients, setRecipients] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [users, setUsers] = useState([]);
    const [editingSettings, setEditingSettings] = useState(false);
    const [busy, setBusy] = useState(false);

    const loadAll = useCallback(() => {
        setIsLoading(true);
        Promise.all([
            clinicService.getSettings(clinicId),
            webInquiryService.getSettings(clinicId),
            webInquiryService.getAllowedDomains(clinicId),
            webInquiryService.getNotifyRecipients(clinicId),
            webInquiryService.getReasonOptions(clinicId),
            clinicService.getUsers(clinicId, 'ALL'),
        ])
            .then(([featureRes, settingsRes, domainsRes, recipientsRes, reasonsRes, usersRes]) => {
                setFeatureEnabled(featureRes.data?.webInquiry === true);
                setSettings(settingsRes.data);
                setDomains(domainsRes.data || []);
                setRecipients(recipientsRes.data || []);
                setReasons(reasonsRes.data || []);
                setUsers(usersRes.data || []);
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Failed to load Web Inquiry settings.');
            })
            .finally(() => setIsLoading(false));
    }, [clinicId]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const handleToggleEnabled = () => {
        if (!settings) return;
        setBusy(true);
        webInquiryService
            .patchEnabled(clinicId, !settings.enabled)
            .then((res) => {
                setSettings(res.data);
                toast.success(res.data.enabled ? 'Widget enabled' : 'Widget disabled');
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Failed to update widget status'))
            .finally(() => setBusy(false));
    };

    const handleRegenerateKey = () => {
        if (!window.confirm('Regenerate the public key? Existing embeds will stop working until updated.')) {
            return;
        }
        setBusy(true);
        webInquiryService
            .regenerateKey(clinicId)
            .then((res) => {
                setSettings(res.data);
                toast.success('Public key regenerated');
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Failed to regenerate key'))
            .finally(() => setBusy(false));
    };

    const handleCopySnippet = async () => {
        if (!settings?.installSnippet) return;
        try {
            await navigator.clipboard.writeText(settings.installSnippet);
            toast.success('Install snippet copied');
        } catch {
            toast.error('Unable to copy snippet');
        }
    };

    const handleTestInquiry = () => {
        setBusy(true);
        webInquiryService
            .sendTestInquiry(clinicId)
            .then((res) => toast.info(res.data?.message || 'Test check complete'))
            .catch((err) => toast.error(err.response?.data?.message || 'Test check failed'))
            .finally(() => setBusy(false));
    };

    if (isLoading) {
        return <Loading />;
    }

    return (
        <div className="col-12">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                <div>
                    <h5 className="clinic-services__title mb-1">
                        <i className="bi bi-globe2 text-primary me-1"></i>
                        Web Inquiry
                    </h5>
                    <p className="text-muted small mb-0">
                        Configure the website inquiry widget, domains, recipients, and reasons for this clinic.
                    </p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleTestInquiry}
                        disabled={busy || !featureEnabled}
                    >
                        <i className="bi bi-lightning me-1"></i>
                        Run config check
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${settings?.enabled ? 'btn-outline-danger' : 'btn-outline-success'}`}
                        onClick={handleToggleEnabled}
                        disabled={busy || !featureEnabled}
                    >
                        <i className={`bi ${settings?.enabled ? 'bi-pause-circle' : 'bi-play-circle'} me-1`}></i>
                        {settings?.enabled ? 'Disable widget' : 'Enable widget'}
                    </button>
                </div>
            </div>

            {!featureEnabled && (
                <div className="alert alert-warning border-0 py-2 px-3 small mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Feature is disabled. Enable <strong>Web Inquiry</strong> under Configuration → Features first.
                </div>
            )}

            <div className="notifications-panel mb-3">
                <div className="notifications-panel__header">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-code-slash"></i>
                        Install
                    </span>
                </div>
                <div className="notifications-panel__body">
                    <div className="clinic-services-toolbar mb-3">
                        <StatusBadge enabled={featureEnabled} labelOn="Feature on" labelOff="Feature off" />
                        <StatusBadge enabled={!!settings?.enabled} labelOn="Widget on" labelOff="Widget off" />
                        <StatusBadge
                            enabled={settings?.installStatus === 'CONNECTED'}
                            labelOn="Connected"
                            labelOff="Not detected"
                        />
                    </div>
                    <InfoRow label="Public key">
                        <code className="small">{settings?.publicKey || '—'}</code>
                        <button
                            type="button"
                            className="btn btn-link btn-sm px-2"
                            onClick={handleRegenerateKey}
                            disabled={busy || !featureEnabled}
                        >
                            Regenerate
                        </button>
                    </InfoRow>
                    <InfoRow label="Last seen">
                        {settings?.lastWidgetSeenAt
                            ? `${settings.lastWidgetSeenAt}${
                                  settings.lastWidgetSeenDomain ? ` · ${settings.lastWidgetSeenDomain}` : ''
                              }`
                            : 'Never'}
                    </InfoRow>
                    <InfoRow label="Embed snippet">
                        <div className="d-flex flex-column gap-2 w-100">
                            <pre className="bg-light border rounded p-2 mb-0 small text-wrap">{settings?.installSnippet}</pre>
                            <div>
                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleCopySnippet}>
                                    <i className="bi bi-clipboard me-1"></i>
                                    Copy snippet
                                </button>
                            </div>
                        </div>
                    </InfoRow>
                </div>
            </div>

            <SettingsPanel
                clinicId={clinicId}
                settings={settings}
                editing={editingSettings}
                setEditing={setEditingSettings}
                onSaved={setSettings}
                disabled={!featureEnabled}
            />

            <DomainsPanel
                clinicId={clinicId}
                domains={domains}
                onChange={setDomains}
                disabled={!featureEnabled}
            />

            <RecipientsPanel
                clinicId={clinicId}
                recipients={recipients}
                users={users}
                onChange={setRecipients}
                disabled={!featureEnabled}
            />

            <ReasonsPanel
                clinicId={clinicId}
                reasons={reasons}
                onChange={setReasons}
                disabled={!featureEnabled}
            />
        </div>
    );
}

function SettingsPanel({ clinicId, settings, editing, setEditing, onSaved, disabled }) {
    const {
        register,
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = useForm({
        defaultValues: settings || {},
    });

    useEffect(() => {
        if (settings) {
            reset(settings);
        }
    }, [settings, reset, editing]);

    const onSubmit = (data) => {
        const payload = {
            ...data,
            escalationMinutes: data.escalationMinutes === '' || data.escalationMinutes == null
                ? null
                : Number(data.escalationMinutes),
            messageMaxLength: Number(data.messageMaxLength),
            retentionMonths: Number(data.retentionMonths),
            rateLimitPerIpHour: Number(data.rateLimitPerIpHour),
            rateLimitPerEmailDay: Number(data.rateLimitPerEmailDay),
            bubbleOffsetX: Number(data.bubbleOffsetX),
            bubbleOffsetY: Number(data.bubbleOffsetY),
        };
        webInquiryService
            .updateSettings(clinicId, payload)
            .then((res) => {
                onSaved(res.data);
                setEditing(false);
                toast.success('Settings saved');
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Failed to save settings'));
    };

    if (!settings) return null;

    return (
        <div className="notifications-panel mb-3">
            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-sliders"></i>
                    Widget settings
                </span>
                <button
                    type="button"
                    className={`btn btn-sm ${editing ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                    onClick={() => setEditing(!editing)}
                    disabled={disabled}
                >
                    <i className={`bi ${editing ? 'bi-x' : 'bi-pencil-square'} me-1`}></i>
                    {editing ? 'Cancel' : 'Edit'}
                </button>
            </div>
            <div className="notifications-panel__body">
                {!editing ? (
                    <>
                        <SectionTitle first>Appearance</SectionTitle>
                        <InfoRow label="Greeting">{settings.greeting || '—'}</InfoRow>
                        <InfoRow label="Bubble label">{settings.bubbleLabel || '—'}</InfoRow>
                        <InfoRow label="Position">{settings.bubblePosition || '—'}</InfoRow>
                        <InfoRow label="Accent color">{settings.accentColor || '—'}</InfoRow>
                        <SectionTitle>Messages</SectionTitle>
                        <InfoRow label="After hours">{settings.afterHoursMessage || '—'}</InfoRow>
                        <InfoRow label="Response expectation">{settings.responseExpectationMessage || '—'}</InfoRow>
                        <InfoRow label="Emergency text">{settings.emergencyText || '—'}</InfoRow>
                        <InfoRow label="Emergency phone">{settings.emergencyPhone || '—'}</InfoRow>
                        <SectionTitle>Consent & reply</SectionTitle>
                        <InfoRow label="Consent text">{settings.consentText || '—'}</InfoRow>
                        <InfoRow label="Privacy URL">{settings.privacyUrl || '—'}</InfoRow>
                        <InfoRow label="Reply from name">{settings.replyFromName || '—'}</InfoRow>
                        <InfoRow label="Patient notify">
                            {[
                                settings.notifyPatientEmailEnabled ? 'Email' : null,
                                settings.notifyPatientSmsEnabled ? 'SMS' : null,
                            ]
                                .filter(Boolean)
                                .join(', ') || 'Off'}
                        </InfoRow>
                        <InfoRow label="Staff reply">
                            {[
                                settings.replyEmailEnabled ? 'Email' : null,
                                settings.replySmsEnabled ? 'SMS' : null,
                            ]
                                .filter(Boolean)
                                .join(', ') || 'Off'}
                        </InfoRow>
                        <SectionTitle>Spam / retention</SectionTitle>
                        <InfoRow label="Turnstile">{settings.turnstileEnabled ? 'On' : 'Off'}</InfoRow>
                        <InfoRow label="Rate limits">
                            {settings.rateLimitPerIpHour}/ip/hr · {settings.rateLimitPerEmailDay}/email/day
                        </InfoRow>
                        <InfoRow label="Retention">{settings.retentionMonths} months</InfoRow>
                    </>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <SectionTitle first>Appearance</SectionTitle>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label className="notifications-form-label">Greeting</label>
                                <input className="form-control form-control-sm" {...register('greeting')} />
                            </div>
                            <div className="col-md-6">
                                <label className="notifications-form-label">Success message</label>
                                <input className="form-control form-control-sm" {...register('successMessage')} />
                            </div>
                            <div className="col-md-4">
                                <label className="notifications-form-label">Bubble label</label>
                                <input className="form-control form-control-sm" {...register('bubbleLabel')} />
                            </div>
                            <div className="col-md-4">
                                <label className="notifications-form-label">Position</label>
                                <select className="form-select form-select-sm" {...register('bubblePosition')}>
                                    <option value="BOTTOM_RIGHT">Bottom right</option>
                                    <option value="BOTTOM_LEFT">Bottom left</option>
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="notifications-form-label">Accent color</label>
                                <input className="form-control form-control-sm" placeholder="#0d6efd" {...register('accentColor')} />
                            </div>
                            <div className="col-md-6">
                                <label className="notifications-form-label">Logo URL override</label>
                                <input className="form-control form-control-sm" {...register('logoUrl')} />
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Language</label>
                                <select className="form-select form-select-sm" {...register('defaultLanguage')}>
                                    <option value="en">English</option>
                                    <option value="fr">French</option>
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Phone country</label>
                                <input className="form-control form-control-sm" maxLength={2} {...register('defaultPhoneCountry')} />
                            </div>
                        </div>

                        <SectionTitle>Form rules</SectionTitle>
                        <div className="row g-2 mb-3">
                            {[
                                ['requireName', 'Require name'],
                                ['requireEmail', 'Require email'],
                                ['requirePhone', 'Require phone'],
                                ['requireMessage', 'Require message'],
                                ['requireConsent', 'Require consent'],
                                ['showReasonPicker', 'Show reason picker'],
                                ['useClinicHours', 'Use clinic hours'],
                            ].map(([name, label]) => (
                                <div className="col-md-4" key={name}>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" id={name} {...register(name)} />
                                        <label className="form-check-label small" htmlFor={name}>
                                            {label}
                                        </label>
                                    </div>
                                </div>
                            ))}
                            <div className="col-md-4">
                                <label className="notifications-form-label">Message max length</label>
                                <input type="number" className="form-control form-control-sm" {...register('messageMaxLength')} />
                            </div>
                        </div>

                        <SectionTitle>Messages</SectionTitle>
                        <div className="row g-3 mb-3">
                            <div className="col-12">
                                <label className="notifications-form-label">Business hours message</label>
                                <textarea className="form-control form-control-sm" rows={2} {...register('businessHoursMessage')} />
                            </div>
                            <div className="col-12">
                                <label className="notifications-form-label">After hours message</label>
                                <textarea className="form-control form-control-sm" rows={2} {...register('afterHoursMessage')} />
                            </div>
                            <div className="col-12">
                                <label className="notifications-form-label">Response expectation</label>
                                <textarea className="form-control form-control-sm" rows={2} {...register('responseExpectationMessage')} />
                            </div>
                            <div className="col-md-8">
                                <label className="notifications-form-label">Emergency text</label>
                                <textarea className="form-control form-control-sm" rows={2} {...register('emergencyText')} />
                            </div>
                            <div className="col-md-4">
                                <label className="notifications-form-label">Emergency phone</label>
                                <input className="form-control form-control-sm" {...register('emergencyPhone')} />
                            </div>
                        </div>

                        <SectionTitle>Consent & channels</SectionTitle>
                        <div className="row g-3 mb-3">
                            <div className="col-12">
                                <label className="notifications-form-label">Consent text</label>
                                <textarea className="form-control form-control-sm" rows={2} {...register('consentText')} />
                            </div>
                            <div className="col-md-6">
                                <label className="notifications-form-label">Privacy URL</label>
                                <input className="form-control form-control-sm" {...register('privacyUrl')} />
                            </div>
                            <div className="col-md-6">
                                <label className="notifications-form-label">Reply from name</label>
                                <input className="form-control form-control-sm" {...register('replyFromName')} />
                            </div>
                            <div className="col-md-6">
                                <label className="notifications-form-label">Reply email from</label>
                                <input className="form-control form-control-sm" {...register('replyEmailFrom')} />
                            </div>
                            {[
                                ['consentAllowsEmail', 'Consent covers email'],
                                ['consentAllowsSms', 'Consent covers SMS'],
                                ['replyEmailEnabled', 'Staff reply by email'],
                                ['replySmsEnabled', 'Staff reply by SMS'],
                                ['notifyPatientEmailEnabled', 'Notify patient by email'],
                                ['notifyPatientSmsEnabled', 'Notify patient by SMS'],
                                ['autoReplyEmailEnabled', 'Auto-reply email'],
                                ['autoReplySmsEnabled', 'Auto-reply SMS'],
                            ].map(([name, label]) => (
                                <div className="col-md-4" key={name}>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" id={name} {...register(name)} />
                                        <label className="form-check-label small" htmlFor={name}>
                                            {label}
                                        </label>
                                    </div>
                                </div>
                            ))}
                            <div className="col-12">
                                <label className="notifications-form-label">Auto-reply email message</label>
                                <textarea className="form-control form-control-sm" rows={2} {...register('autoReplyEmailMessage')} />
                            </div>
                            <div className="col-12">
                                <label className="notifications-form-label">Auto-reply SMS message</label>
                                <input className="form-control form-control-sm" maxLength={320} {...register('autoReplySmsMessage')} />
                            </div>
                        </div>

                        <SectionTitle>Spam / retention</SectionTitle>
                        <div className="row g-3 mb-3">
                            <div className="col-md-3">
                                <div className="form-check form-switch mt-4">
                                    <input className="form-check-input" type="checkbox" id="turnstileEnabled" {...register('turnstileEnabled')} />
                                    <label className="form-check-label small" htmlFor="turnstileEnabled">
                                        Turnstile
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Per IP / hour</label>
                                <input type="number" className="form-control form-control-sm" {...register('rateLimitPerIpHour')} />
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Per email / day</label>
                                <input type="number" className="form-control form-control-sm" {...register('rateLimitPerEmailDay')} />
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Retention (months)</label>
                                <input type="number" className="form-control form-control-sm" {...register('retentionMonths')} />
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Escalation (minutes)</label>
                                <input type="number" className="form-control form-control-sm" {...register('escalationMinutes')} />
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Bubble offset X</label>
                                <input type="number" className="form-control form-control-sm" {...register('bubbleOffsetX')} />
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Bubble offset Y</label>
                                <input type="number" className="form-control form-control-sm" {...register('bubbleOffsetY')} />
                            </div>
                        </div>

                        <div className="clinic-services-form-actions">
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(false)}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-sm btn-success" disabled={isSubmitting}>
                                {isSubmitting ? 'Saving…' : 'Save settings'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

function DomainsPanel({ clinicId, domains, onChange, disabled }) {
    const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm();
    const [showForm, setShowForm] = useState(false);

    const refresh = () =>
        webInquiryService.getAllowedDomains(clinicId).then((res) => onChange(res.data || []));

    const onSubmit = (data) => {
        webInquiryService
            .createAllowedDomain(clinicId, { domain: data.domain, isActive: true })
            .then(() => {
                toast.success('Domain added');
                reset();
                setShowForm(false);
                return refresh();
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Failed to add domain'));
    };

    const toggleActive = (domain) => {
        webInquiryService
            .updateAllowedDomain(clinicId, domain.id, { domain: domain.domain, isActive: !domain.isActive })
            .then(refresh)
            .catch((err) => toast.error(err.response?.data?.message || 'Failed to update domain'));
    };

    const remove = (domain) => {
        if (!window.confirm(`Remove ${domain.domain}?`)) return;
        webInquiryService
            .deleteAllowedDomain(clinicId, domain.id)
            .then(() => {
                toast.success('Domain removed');
                return refresh();
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Failed to delete domain'));
    };

    return (
        <div className="notifications-panel mb-3">
            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-globe"></i>
                    Allowed domains
                </span>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={disabled}
                    onClick={() => setShowForm(!showForm)}
                >
                    <i className="bi bi-plus me-1"></i>
                    Add
                </button>
            </div>
            <div className="notifications-panel__body">
                {showForm && (
                    <form className="mb-3" onSubmit={handleSubmit(onSubmit)}>
                        <div className="row g-2 align-items-end">
                            <div className="col-md-8">
                                <label className="notifications-form-label">Domain</label>
                                <input
                                    className="form-control form-control-sm"
                                    placeholder="www.clinic.com"
                                    {...register('domain', { required: true })}
                                />
                            </div>
                            <div className="col-md-4">
                                <button type="submit" className="btn btn-sm btn-success" disabled={isSubmitting}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </form>
                )}
                {domains.length === 0 ? (
                    <div className="clinic-booking-empty">
                        <p className="text-muted small mb-0">No allowed domains yet.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Domain</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {domains.map((d) => (
                                    <tr key={d.id}>
                                        <td>
                                            <code className="small">{d.domain}</code>
                                        </td>
                                        <td>
                                            <StatusBadge enabled={d.isActive} />
                                        </td>
                                        <td className="text-end">
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm"
                                                disabled={disabled}
                                                onClick={() => toggleActive(d)}
                                            >
                                                {d.isActive ? 'Disable' : 'Enable'}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm text-danger"
                                                disabled={disabled}
                                                onClick={() => remove(d)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function RecipientsPanel({ clinicId, recipients, users, onChange, disabled }) {
    const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm({
        defaultValues: { notifyEmail: true, notifyInApp: true },
    });
    const [showForm, setShowForm] = useState(false);

    const refresh = () =>
        webInquiryService.getNotifyRecipients(clinicId).then((res) => onChange(res.data || []));

    const onSubmit = (data) => {
        const payload = {
            userId: data.userId || null,
            email: data.email || null,
            notifyEmail: !!data.notifyEmail,
            notifyInApp: !!data.notifyInApp,
            isActive: true,
        };
        webInquiryService
            .createNotifyRecipient(clinicId, payload)
            .then(() => {
                toast.success('Recipient added');
                reset({ notifyEmail: true, notifyInApp: true });
                setShowForm(false);
                return refresh();
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Failed to add recipient'));
    };

    const remove = (item) => {
        if (!window.confirm('Remove this recipient?')) return;
        webInquiryService
            .deleteNotifyRecipient(clinicId, item.id)
            .then(() => {
                toast.success('Recipient removed');
                return refresh();
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Failed to delete recipient'));
    };

    return (
        <div className="notifications-panel mb-3">
            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-people"></i>
                    Notify recipients
                </span>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={disabled}
                    onClick={() => setShowForm(!showForm)}
                >
                    <i className="bi bi-plus me-1"></i>
                    Add
                </button>
            </div>
            <div className="notifications-panel__body">
                {showForm && (
                    <form className="mb-3" onSubmit={handleSubmit(onSubmit)}>
                        <div className="row g-2">
                            <div className="col-md-6">
                                <label className="notifications-form-label">Staff user</label>
                                <select className="form-select form-select-sm" {...register('userId')}>
                                    <option value="">Optional</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.username}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="notifications-form-label">Shared inbox email</label>
                                <input type="email" className="form-control form-control-sm" {...register('email')} />
                            </div>
                            <div className="col-md-4">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" id="notifyEmail" {...register('notifyEmail')} />
                                    <label className="form-check-label small" htmlFor="notifyEmail">
                                        Email notify
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" id="notifyInApp" {...register('notifyInApp')} />
                                    <label className="form-check-label small" htmlFor="notifyInApp">
                                        In-app notify
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-4 text-end">
                                <button type="submit" className="btn btn-sm btn-success" disabled={isSubmitting}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </form>
                )}
                {recipients.length === 0 ? (
                    <div className="clinic-booking-empty">
                        <p className="text-muted small mb-0">No notify recipients yet.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Recipient</th>
                                    <th>Channels</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {recipients.map((r) => (
                                    <tr key={r.id}>
                                        <td className="small">
                                            {r.userName || r.email || '—'}
                                            {r.userName && r.email ? (
                                                <div className="text-muted">{r.email}</div>
                                            ) : null}
                                        </td>
                                        <td className="small">
                                            {[r.notifyEmail ? 'Email' : null, r.notifyInApp ? 'In-app' : null]
                                                .filter(Boolean)
                                                .join(', ') || '—'}
                                        </td>
                                        <td>
                                            <StatusBadge enabled={r.isActive} />
                                        </td>
                                        <td className="text-end">
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm text-danger"
                                                disabled={disabled}
                                                onClick={() => remove(r)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function ReasonsPanel({ clinicId, reasons, onChange, disabled }) {
    const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm({
        defaultValues: { isActive: true, isUrgent: false, sortOrder: 0 },
    });
    const [showForm, setShowForm] = useState(false);

    const refresh = () =>
        webInquiryService.getReasonOptions(clinicId).then((res) => onChange(res.data || []));

    const onSubmit = (data) => {
        webInquiryService
            .createReasonOption(clinicId, {
                code: data.code,
                labelEn: data.labelEn,
                labelFr: data.labelFr || null,
                sortOrder: Number(data.sortOrder || 0),
                isActive: !!data.isActive,
                isUrgent: !!data.isUrgent,
            })
            .then(() => {
                toast.success('Reason added');
                reset({ isActive: true, isUrgent: false, sortOrder: 0 });
                setShowForm(false);
                return refresh();
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Failed to add reason'));
    };

    const remove = (item) => {
        if (!window.confirm(`Remove reason ${item.code}?`)) return;
        webInquiryService
            .deleteReasonOption(clinicId, item.id)
            .then(() => {
                toast.success('Reason removed');
                return refresh();
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Failed to delete reason'));
    };

    return (
        <div className="notifications-panel mb-3">
            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-list-check"></i>
                    Reason options
                </span>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={disabled}
                    onClick={() => setShowForm(!showForm)}
                >
                    <i className="bi bi-plus me-1"></i>
                    Add
                </button>
            </div>
            <div className="notifications-panel__body">
                {showForm && (
                    <form className="mb-3" onSubmit={handleSubmit(onSubmit)}>
                        <div className="row g-2">
                            <div className="col-md-3">
                                <label className="notifications-form-label">Code</label>
                                <input className="form-control form-control-sm" placeholder="BOOK_EXAM" {...register('code', { required: true })} />
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Label (EN)</label>
                                <input className="form-control form-control-sm" {...register('labelEn', { required: true })} />
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Label (FR)</label>
                                <input className="form-control form-control-sm" {...register('labelFr')} />
                            </div>
                            <div className="col-md-3">
                                <label className="notifications-form-label">Sort</label>
                                <input type="number" className="form-control form-control-sm" {...register('sortOrder')} />
                            </div>
                            <div className="col-md-3">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" id="reasonUrgent" {...register('isUrgent')} />
                                    <label className="form-check-label small" htmlFor="reasonUrgent">
                                        Urgent
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <button type="submit" className="btn btn-sm btn-success" disabled={isSubmitting}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </form>
                )}
                {reasons.length === 0 ? (
                    <div className="clinic-booking-empty">
                        <p className="text-muted small mb-0">No reason options yet.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Label</th>
                                    <th>Sort</th>
                                    <th>Flags</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {reasons.map((r) => (
                                    <tr key={r.id}>
                                        <td>
                                            <code className="small">{r.code}</code>
                                        </td>
                                        <td className="small">
                                            {r.labelEn}
                                            {r.labelFr ? <div className="text-muted">{r.labelFr}</div> : null}
                                        </td>
                                        <td className="small">{r.sortOrder}</td>
                                        <td className="small">
                                            {[r.isActive ? 'Active' : 'Inactive', r.isUrgent ? 'Urgent' : null]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </td>
                                        <td className="text-end">
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm text-danger"
                                                disabled={disabled}
                                                onClick={() => remove(r)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default WebInquiryTab;
